// Client-side catalogue of the `excel_*` tools (PLAN-agent-v2 §4.3): friendly labels for the
// cards/indicators and which input keys carry the target range (for highlights and the journal).
// Keep in sync with peerception-api/api/agent_v2/tools/*.py (#17).
import { TOOL_ASK_USER, type RiskClass } from '../../types/agent';
import { boundingBox, isValidAddress } from '../excel/a1';

export interface ToolMeta {
  /** Present-participle label ("Writing values"). */
  label: string;
  risk: RiskClass;
  /** Input keys holding the sheet / address of the affected range, when any. */
  sheetKey?: string;
  addressKey?: string;
  /** Input key holding a {A1 cell: …} map instead of one address: the target is their bounding box. */
  cellsKey?: string;
  /** True when the before-image of the target range is journalled (undoable). */
  journalled?: boolean;
}

export const TOOL_CATALOGUE: Record<string, ToolMeta> = {
  // Read
  excel_get_workbook_overview: { label: 'Reading workbook overview', risk: 'read' },
  excel_get_sheet_summary: { label: 'Reading sheet summary', risk: 'read', sheetKey: 'sheet' },
  excel_scan_sheet: { label: 'Scanning sheet', risk: 'read', sheetKey: 'sheet' },
  excel_read_sync_anchors: { label: 'Reading sync anchors', risk: 'read' },
  excel_read_range: { label: 'Reading range', risk: 'read', sheetKey: 'sheet', addressKey: 'address' },
  excel_get_selection: { label: 'Reading selection', risk: 'read' },
  excel_find_cells: { label: 'Searching cells', risk: 'read' },
  excel_get_named_items: { label: 'Listing named ranges', risk: 'read' },
  excel_list_tables: { label: 'Listing tables', risk: 'read' },
  excel_get_table: { label: 'Reading table', risk: 'read' },
  excel_get_precedents: { label: 'Tracing precedents', risk: 'read', sheetKey: 'sheet', addressKey: 'cell' },
  excel_get_dependents: { label: 'Tracing dependents', risk: 'read', sheetKey: 'sheet', addressKey: 'cell' },
  excel_get_precedents_batch: { label: 'Tracing precedents (batch)', risk: 'read', sheetKey: 'sheet' },
  excel_get_dependents_batch: { label: 'Tracing dependents (batch)', risk: 'read', sheetKey: 'sheet' },
  excel_scan_errors: { label: 'Scanning for errors', risk: 'read' },
  excel_get_range_format: { label: 'Reading formatting', risk: 'read', sheetKey: 'sheet', addressKey: 'address' },
  excel_list_charts: { label: 'Listing charts', risk: 'read' },
  excel_get_comments: { label: 'Reading comments', risk: 'read' },
  excel_get_change_journal: { label: 'Reading change journal', risk: 'read' },
  // Audit (AUDIT_SPEC §2.1)
  excel_capture_sheet: { label: 'Capturing sheet', risk: 'read', sheetKey: 'sheet' },
  excel_get_workbook_integrity: { label: 'Checking workbook integrity', risk: 'read' },
  excel_inspect_cell: { label: 'Inspecting cell', risk: 'read', sheetKey: 'sheet', addressKey: 'cell' },
  excel_write_audit_sheet: { label: 'Writing audit sheet', risk: 'write', sheetKey: 'sheet_name' },
  // Write — cells & formulas
  excel_write_range: { label: 'Writing values', risk: 'write', sheetKey: 'sheet', addressKey: 'address', journalled: true },
  excel_write_formulas: { label: 'Writing formulas', risk: 'write', sheetKey: 'sheet', addressKey: 'address', journalled: true },
  excel_write_cells: { label: 'Writing cells', risk: 'write', sheetKey: 'sheet', cellsKey: 'cells', journalled: true },
  excel_write_block: { label: 'Writing block', risk: 'write', sheetKey: 'sheet', addressKey: 'anchor', journalled: true },
  excel_fill_range: { label: 'Filling range', risk: 'write', sheetKey: 'sheet', addressKey: 'destination', journalled: true },
  excel_copy_range: { label: 'Copying range', risk: 'write', sheetKey: 'dest_sheet', addressKey: 'dest' },
  excel_clear_range: { label: 'Clearing range', risk: 'write', sheetKey: 'sheet', addressKey: 'address', journalled: true },
  excel_set_number_format: { label: 'Setting number format', risk: 'write', sheetKey: 'sheet', addressKey: 'address', journalled: true },
  excel_format_range: { label: 'Formatting range', risk: 'write', sheetKey: 'sheet', addressKey: 'address' },
  excel_set_column_widths: { label: 'Setting column widths', risk: 'write', sheetKey: 'sheet' },
  excel_set_row_heights: { label: 'Setting row heights', risk: 'write', sheetKey: 'sheet' },
  excel_merge_cells: { label: 'Merging cells', risk: 'write', sheetKey: 'sheet', addressKey: 'address' },
  excel_unmerge_cells: { label: 'Unmerging cells', risk: 'write', sheetKey: 'sheet', addressKey: 'address' },
  excel_add_data_validation: { label: 'Adding data validation', risk: 'write', sheetKey: 'sheet', addressKey: 'address' },
  excel_add_comment: { label: 'Adding comment', risk: 'write', sheetKey: 'sheet', addressKey: 'cell' },
  excel_delete_comment: { label: 'Deleting comment', risk: 'write', sheetKey: 'sheet', addressKey: 'cell' },
  excel_undo_last_change: { label: 'Undoing last change', risk: 'destructive' },
  // The server's undo of an operation restores before-images through this one (sculpt-api operations/undo.py);
  // the model never calls it. Journalled like a write, so a restore can itself be taken back.
  excel_restore_snapshot: { label: 'Restoring range', risk: 'write', sheetKey: 'sheet', addressKey: 'address', journalled: true },
  // Write — structure
  excel_insert_rows: { label: 'Inserting rows', risk: 'write', sheetKey: 'sheet' },
  excel_insert_columns: { label: 'Inserting columns', risk: 'write', sheetKey: 'sheet' },
  excel_add_object_column: { label: 'Adding object column', risk: 'write', sheetKey: 'sheet' },
  excel_delete_rows: { label: 'Deleting rows', risk: 'destructive', sheetKey: 'sheet', addressKey: 'address' },
  excel_delete_columns: { label: 'Deleting columns', risk: 'destructive', sheetKey: 'sheet', addressKey: 'address' },
  excel_create_sheet: { label: 'Creating sheet', risk: 'write' },
  excel_rename_sheet: { label: 'Renaming sheet', risk: 'write', sheetKey: 'sheet' },
  excel_move_sheet: { label: 'Moving sheet', risk: 'write', sheetKey: 'sheet' },
  excel_set_sheet_visibility: { label: 'Changing sheet visibility', risk: 'write', sheetKey: 'sheet' },
  excel_delete_sheet: { label: 'Deleting sheet', risk: 'destructive' },
  // A new model from a template, into an empty workbook (sculpt-api `sculpt/templates`). Not journalled: it inserts
  // and deletes sheets, which the change journal cannot take back.
  excel_insert_model: { label: 'Inserting a new model', risk: 'write' },
  excel_activate_sheet: { label: 'Opening sheet', risk: 'navigation', sheetKey: 'sheet' },
  excel_select_range: { label: 'Selecting range', risk: 'navigation', sheetKey: 'sheet', addressKey: 'address' },
  excel_freeze_panes: { label: 'Freezing panes', risk: 'write', sheetKey: 'sheet' },
  excel_define_name: { label: 'Defining name', risk: 'write', sheetKey: 'sheet', addressKey: 'address' },
  excel_delete_name: { label: 'Deleting name', risk: 'destructive' },
  excel_create_table: { label: 'Creating table', risk: 'write', sheetKey: 'sheet', addressKey: 'address' },
  excel_add_table_rows: { label: 'Adding table rows', risk: 'write' },
  excel_delete_table: { label: 'Deleting table', risk: 'destructive' },
  excel_create_chart: { label: 'Creating chart', risk: 'write', sheetKey: 'sheet', addressKey: 'data_range' },
  excel_update_chart: { label: 'Updating chart', risk: 'write', sheetKey: 'sheet' },
  excel_delete_chart: { label: 'Deleting chart', risk: 'destructive', sheetKey: 'sheet' },
  excel_group_rows: { label: 'Grouping rows', risk: 'write', sheetKey: 'sheet', addressKey: 'address' },
  excel_group_columns: { label: 'Grouping columns', risk: 'write', sheetKey: 'sheet', addressKey: 'address' },
  excel_set_protection: { label: 'Changing sheet protection', risk: 'write', sheetKey: 'sheet' },
  excel_recalculate: { label: 'Recalculating', risk: 'navigation' },
  excel_converge: { label: 'Converging circular block', risk: 'write', sheetKey: 'sheet', addressKey: 'circ_range', journalled: true },
  excel_set_calculation_mode: { label: 'Setting calculation mode', risk: 'write' },
  // Server tools (no client execution; indicator only)
  fm_reference: { label: 'Consulting modeling reference', risk: 'read' },
  // Attached documents (2026-09-10) — server tools, never executed by the client.
  doc_list: { label: 'Listing attached documents', risk: 'read' },
  doc_read: { label: 'Reading an attached document', risk: 'read' },
  doc_search: { label: 'Searching the attached documents', risk: 'read' },
  fm_lint_formulas: { label: 'Checking formulas', risk: 'read' },

  // ── The index read tools (sculpt-api `sculpt/workbook/tools.py`) ──────────────────────────────────────
  // They run on the server, against the index built at pane open, and never touch Excel. They were absent
  // here, so every one of them drew "Running describe sheet" — when it drew anything at all, which before
  // the `tool_call` event it did not: a turn could spend a minute reading and show only "Thinking…".
  find: { label: 'Searching the model', risk: 'read' },
  outline: { label: 'Reading the outline', risk: 'read', sheetKey: 'sheet' },
  describe_sheet: { label: 'Reading a sheet', risk: 'read', sheetKey: 'sheet' },
  read_zone: { label: 'Reading a zone', risk: 'read', sheetKey: 'sheet' },
  read_range: { label: 'Reading a range', risk: 'read', sheetKey: 'sheet', addressKey: 'address' },
  cell: { label: 'Reading a cell', risk: 'read', sheetKey: 'sheet', addressKey: 'cell' },
  trace: { label: 'Tracing a formula', risk: 'read', sheetKey: 'sheet', addressKey: 'cell' },
  zone_links: { label: 'Following the links', risk: 'read', sheetKey: 'sheet' },
  resolve: { label: 'Resolving a label', risk: 'read', sheetKey: 'sheet' },
  resolve_object: { label: 'Resolving an object', risk: 'read' },
  period_map: { label: 'Aligning the periods', risk: 'read', sheetKey: 'sheet' },
  siblings: { label: 'Looking for a zone to mirror', risk: 'read', sheetKey: 'sheet' },
  placement: { label: 'Looking for a place', risk: 'read', sheetKey: 'sheet' },
  conventions: { label: 'Reading the house style', risk: 'read', sheetKey: 'sheet' },
  health: { label: 'Checking the model', risk: 'read', sheetKey: 'sheet' },
  structural_audit: { label: 'Auditing the structure', risk: 'read', sheetKey: 'sheet' },
  drivers: { label: 'Tracing what drives the number', risk: 'read', sheetKey: 'sheet' },
  metrics: { label: 'Reading the key indicators', risk: 'read' },
  model_map: { label: 'Mapping the model', risk: 'read' },
  reconcile_terms: { label: 'Comparing the model with the term sheet', risk: 'read' },
  preview_overwrite: { label: 'Checking what a write would cover', risk: 'read', sheetKey: 'sheet', addressKey: 'address' },
  check_formula: { label: 'Checking a formula', risk: 'read', sheetKey: 'sheet' },

  // ── The reference base (sculpt-api `sculpt/knowledge/tools.py`) ───────────────────────────────────────
  // Not the user's workbook but what a component IS — its inputs, its lines, its pitfalls. Named apart from
  // `find` for that reason: on a step line the two are a minute of reading each, and which one the agent is
  // in says whether it is still working out the model or already reading up on how to build the thing.
  knowledge: { label: 'Reading the modelling reference', risk: 'read' },

  // ── The object operations (sculpt-api `sculpt/agent/operations/tools.py`) ─────────────────────────────
  // The longest wait of a turn is between these two: `start_*` builds the brief, and the model then writes
  // the whole Change Plan in one answer.
  // The Question tool (sculpt-api PLAN-questions.md). It reaches the pane as a client call but nothing runs
  // it: `ChatSession` draws a card and answers it from what the user fills in. The executor has no method of
  // that name, so no handler is registered for it — the label is here for the rare step-line fallback.
  ask_user: { label: 'Asking you', risk: 'read' },

  start_add: { label: 'Preparing to add', risk: 'read' },
  start_edit: { label: 'Preparing the change', risk: 'read' },
  start_delete: { label: 'Preparing the removal', risk: 'read' },
  submit_change_plan: { label: 'Building the plan', risk: 'read' },
  undo_operation: { label: 'Taking the operation back', risk: 'destructive' },
  // A manual write declared (sculpt-api `sculpt/agent/manual.py`): the reason and the sheets come as a card.
  start_manual: { label: 'Declaring a write by hand', risk: 'read' },

  // The method of a task (sculpt-api `sculpt/skills`) and an attached document read by pages
  // (`sculpt/documents`): both drew their raw name.
  read_skill: { label: 'Preparing the method', risk: 'read' },
  read_document: { label: 'Reading an attached document', risk: 'read' },

  // The template a new model is built from (sculpt-api `sculpt/templates/tools.py`): its Assumptions and defaults,
  // read before `excel_insert_model`.
  model_template: { label: 'Reading the model template', risk: 'read' },
};

/** Server tools by name — the ones above that the add-in never executes. Kept next to the catalogue so
 *  `isServerTool` does not have to guess from a prefix that sculpt's tools do not carry. */
export const SERVER_TOOL_NAMES: ReadonlySet<string> = new Set([
  'find', 'outline', 'describe_sheet', 'read_zone', 'read_range', 'cell', 'trace', 'zone_links', 'resolve',
  'resolve_object', 'period_map', 'siblings', 'placement', 'conventions', 'health', 'structural_audit', 'drivers', 'metrics', 'model_map', 'reconcile_terms', 'preview_overwrite',
  'check_formula', 'knowledge', 'start_add', 'start_edit', 'start_delete', 'submit_change_plan',
  'undo_operation', 'start_manual', 'read_skill', 'read_document', 'model_template',
]);

/** Tools the user answers instead of the pane running them: the Question card (sculpt-api
 *  PLAN-questions.md). They are in the catalogue for their label, and have no executor method. */
export const ANSWERED_TOOL_NAMES: ReadonlySet<string> = new Set([TOOL_ASK_USER]);

export function isAnsweredTool(toolName: string): boolean {
  return ANSWERED_TOOL_NAMES.has(toolName);
}

/** Friendly label → fallback to a humanised tool name. */
/** Server-executed tools never reach the client executor: the legacy `fm_*` / `map_*` / `doc_*` families,
 *  and sculpt's index and operation tools, which carry no prefix at all. */
export function isServerTool(toolName: string): boolean {
  return /^(fm|map|doc)_/.test(toolName) || SERVER_TOOL_NAMES.has(toolName);
}

/** The label of a call the catalogue does not know. Never the tool's name: how the agent is built stays
 *  internal (sculpt-api PLAN-guardrails.md D2b), so an unknown call reads as work on the workbook. */
export const NEUTRAL_TOOL_LABEL = 'Working on your workbook';

export function toolLabel(toolName: string): string {
  return TOOL_CATALOGUE[toolName]?.label ?? NEUTRAL_TOOL_LABEL;
}

/** Sheet + address a call targets, when derivable from its input. */
export function toolTarget(toolName: string, input: Record<string, unknown>): { sheet: string; address: string } | null {
  const meta = TOOL_CATALOGUE[toolName];
  if (!meta?.sheetKey) return null;
  const sheet = input[meta.sheetKey];
  if (typeof sheet !== 'string' || !sheet) return null;
  if (meta.cellsKey) {
    // Scattered targets ({cell: value}): the affected range is the rectangle covering them.
    const cells = input[meta.cellsKey];
    if (!cells || typeof cells !== 'object' || Array.isArray(cells)) return null;
    const addresses = Object.keys(cells as Record<string, unknown>).filter((a) => isValidAddress(a));
    if (!addresses.length) return null;
    try {
      return { sheet, address: boundingBox(addresses) };
    } catch {
      return null;
    }
  }
  const address = meta.addressKey ? input[meta.addressKey] : 'A1';
  return { sheet, address: typeof address === 'string' && address ? address : 'A1' };
}
